import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { onSocketEvent } from '../hooks/useWebSocket'
import api from '../lib/api'
import {
  MessageSquare,
  UserCircle,
  Bot,
  Search,
  Archive,
  Globe,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Download,
  XCircle,
  Flag,
  Pencil,
  Check,
  Trash2,
  Loader2
} from 'lucide-react'
import {
  useUserTimezone,
  formatTimeInUserTz,
  formatDateInUserTz,
} from '@/hooks/useUserTimezone'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface Conversation {
  id: string
  visitorId: string
  clientId: string
  status: string
  createdAt: string
  endedAt: string | null
  metadata: any
  agentId: string | null
  messageCount: number
  lastMessageAt: string | null
  snippet?: string
}

interface ConversationDetail {
  id: string
  visitorId: string
  clientId: string
  status: string
  createdAt: string
  endedAt: string | null
  metadata: any
  agentId: string | null
  messages: Message[]
}

interface Message {
  id: string
  content: string
  senderType: 'visitor' | 'agent' | 'bot'
  senderId: string
  timestamp: string
  metadata: any
}

interface ConversationsPageProps {
  /** True when rendered inside ChatHubPage's tab — suppresses the local
      page header so we don't double up with the hub's PageHeader. */
  embedded?: boolean
}

export default function ConversationsPage({ embedded = false }: ConversationsPageProps = {}) {
  const { agent } = useAuth()
  const { toast } = useToast()
  const userTz = useUserTimezone()

  const handleFlagMessage = async (conversationId: string, messageId: string, classification: string, visitorId?: string) => {
    try {
      await api.post('/api/chat/classify-message', { sessionId: conversationId, messageId, classification })
      toast({
        title: 'Message flagged',
        description: `Marked as ${classification.replace('_', ' ')}.`,
        action: visitorId && classification !== 'off_topic' ? (
          <Button variant="destructive" size="sm" onClick={() => handleBlockVisitor(visitorId, classification)}>
            Block Visitor
          </Button>
        ) : undefined
      })
    } catch {
      toast({ title: 'Error', description: 'Failed to flag message.', variant: 'destructive' })
    }
  }

  const handleBlockVisitor = async (visitorId: string, reason: string) => {
    try {
      await api.post('/api/agent/blocked-visitors', { visitorId, reason })
      toast({ title: 'Visitor blocked', description: 'They will no longer receive AI responses.' })
    } catch {
      toast({ title: 'Error', description: 'Failed to block visitor.', variant: 'destructive' })
    }
  }

  const handleSaveName = async () => {
    if (!selectedConversation) return
    if (!editNameValue.trim()) {
      toast({ title: 'Name cannot be empty', description: 'Please enter a valid name.', variant: 'destructive' })
      return
    }
    try {
      await api.put(`/api/agent/conversations/${selectedConversation.id}/name`, { name: editNameValue.trim() })
      // Update local state
      const nameParts = editNameValue.trim().split(/\s+/)
      setSelectedConversation(prev => prev ? {
        ...prev,
        metadata: {
          ...prev.metadata,
          name: editNameValue.trim(),
          contactInfo: { ...prev.metadata?.contactInfo, firstName: nameParts[0], lastName: nameParts.slice(1).join(' ') || undefined }
        }
      } : null)
      // Update in the list too
      setConversations(prev => prev.map(c => c.id === selectedConversation.id ? {
        ...c,
        metadata: { ...c.metadata, name: editNameValue.trim(), contactInfo: { ...c.metadata?.contactInfo, firstName: nameParts[0] } }
      } : c))
      setEditingName(false)
      toast({ title: 'Name updated' })
    } catch {
      toast({ title: 'Error', description: 'Failed to update name.', variant: 'destructive' })
    }
  }
  const [searchParams, setSearchParams] = useSearchParams()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<ConversationDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fetchConversationsRef = useRef<() => void>(() => {})
  const [editingName, setEditingName] = useState(false)
  const [editNameValue, setEditNameValue] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const dateFilter = searchParams.get('filter') // 'today' or null
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize] = useState(50)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [showMobileDetail, setShowMobileDetail] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      // Clear selection BEFORE the API call to prevent the detail panel
      // from rendering a deleted conversation during the re-fetch
      const wasSelected = selectedConversation?.id === deleteTarget
      if (wasSelected) {
        setSelectedConversation(null)
        setShowMobileDetail(false)
      }
      await api.delete(`/api/agent/conversations/${deleteTarget}`)
      // Remove from local list immediately (optimistic) so UI doesn't flash
      setConversations(prev => prev.filter(c => c.id !== deleteTarget))
      toast({ title: 'Deleted', description: 'Conversation deleted.' })
      // Re-fetch in background to sync with server
      fetchConversations()
    } catch {
      // If delete failed and we optimistically removed, re-fetch to restore
      fetchConversations()
      toast({ title: 'Error', description: 'Failed to delete.', variant: 'destructive' })
    } finally {
      setDeleting(false)
      setDeleteTarget(null)
    }
  }

  useEffect(() => {
    fetchConversations()
  }, [currentPage, statusFilter, dateFilter])

  useEffect(() => {
    // Scroll only the Radix ScrollArea viewport, not the page. scrollIntoView
    // bubbles up to whatever scrollable ancestor it finds first — on the
    // conversations page that's the *outer* page scroller, so picking a new
    // conversation used to drag the whole dashboard down with it.
    const end = messagesEndRef.current
    if (!end) return
    const viewport = end.closest('[data-radix-scroll-area-viewport]') as HTMLElement | null
    if (viewport) {
      viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' })
    } else {
      // Fallback if rendered outside Radix ScrollArea.
      end.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selectedConversation?.messages])

  // Real-time sync: refresh list when a chat is completed
  // Uses ref so the listener always calls the latest fetchConversations
  // without needing it (or its closed-over state) in the dependency array
  useEffect(() => {
    const cleanup = onSocketEvent('chat:completed', () => {
      fetchConversationsRef.current()
    })
    return cleanup
  }, [])

  // Deep-link: auto-select conversation from URL param (e.g. ?id=uuid)
  useEffect(() => {
    const conversationId = searchParams.get('id')
    if (conversationId && !selectedConversation) {
      fetchConversationDetails(conversationId)
      setShowMobileDetail(true)
      searchParams.delete('id')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, selectedConversation])

  const fetchConversations = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: (currentPage * pageSize).toString(),
      })

      if (statusFilter !== 'all') {
        params.append('status', statusFilter)
      }

      if (dateFilter === 'today') {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        params.append('since', today.toISOString())
      }

      const response = await api.get(`/api/agent/conversations?${params}`)

      if (response.data.success) {
        setConversations(response.data.conversations || [])
      }
    } catch {
      // Error handled silently — list remains as-is
    } finally {
      setLoading(false)
    }
  }

  // Keep ref current so socket listeners always call the latest version
  fetchConversationsRef.current = fetchConversations

  const fetchConversationDetails = async (conversationId: string) => {
    setEditingName(false)
    setEditNameValue('')
    try {
      const response = await api.get(`/api/agent/conversations/${conversationId}/messages`)

      if (response.data.success) {
        setSelectedConversation(response.data.conversation)
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error)
    }
  }

  const handleConversationClick = (conversation: Conversation) => {
    fetchConversationDetails(conversation.id)
  }

  const filteredConversations = conversations.filter(conv => {
    if (!debouncedSearch) return true
    const q = debouncedSearch.toLowerCase()
    const m = conv.metadata || {}
    const searchable = [
      conv.visitorId,
      conv.id,
      m?.contactInfo?.firstName,
      m?.contactInfo?.lastName,
      m?.contactInfo?.email,
      m?.contactInfo?.companyName,
      m?.firstName,
      m?.name,
    ].filter(Boolean).map(v => String(v).toLowerCase()).join(' ')
    return searchable.includes(q)
  })

  const getParticipantName = (conv: Conversation): string => {
    const m = conv.metadata
    if (m?.contactInfo?.firstName) {
      const fullName = m.contactInfo.lastName ? `${m.contactInfo.firstName} ${m.contactInfo.lastName}` : m.contactInfo.firstName
      return fullName + (m.contactInfo.companyName ? ` · ${m.contactInfo.companyName}` : '')
    }
    if (m?.name) return m.name
    if (m?.firstName) return m.firstName
    return 'Website Visitor'
  }

  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000)
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    if (mins === 0) return `${secs}s`
    return `${mins}m ${secs}s`
  }

  const getInitials = (conv: Conversation): string => {
    const name = getParticipantName(conv)
    if (name === 'Website Visitor') return 'WV'
    return name.charAt(0).toUpperCase()
  }

  // Keep backward-compatible helper for detail view where we only have visitorId
  const getInitialsFromId = (id: string) => id.slice(-2).toUpperCase()

  const getStatusBadge = (status: string) => {
    const isLive = status === 'active'
    return (
      <Badge className={`${isLive ? 'bg-green-500' : 'bg-blue-500'} text-white text-xs`}>
        {isLive ? 'Live' : 'Completed'}
      </Badge>
    )
  }

  const handleConversationSelect = (conversation: Conversation) => {
    handleConversationClick(conversation)
    setShowMobileDetail(true)
  }

  const handleBackToList = () => {
    setShowMobileDetail(false)
    setSelectedConversation(null)
  }

  const handleExportCSV = () => {
    if (conversations.length === 0) return
    const headers = ['ID', 'Visitor', 'Status', 'Messages', 'Started', 'Ended']
    const rows = conversations.map(c => [
      c.id,
      c.visitorId,
      c.status,
      c.messageCount || 0,
      c.createdAt ? new Date(c.createdAt).toLocaleString('sv-SE', { timeZone: userTz }) : '',
      c.endedAt ? new Date(c.endedAt).toLocaleString('sv-SE', { timeZone: userTz }) : ''
    ])
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `conversations-${new Date().toLocaleDateString('en-CA', { timeZone: userTz })}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className={embedded
      ? 'h-full flex flex-col'
      : 'h-[calc(100dvh-13rem-env(safe-area-inset-bottom))] sm:h-[calc(100dvh-12rem-env(safe-area-inset-bottom))] md:h-[calc(100vh-4rem)] flex flex-col'
    }>
      {/* Header — only when rendered standalone. ChatHubPage owns the
          PageHeader when this is mounted in the tab hub. */}
      {!embedded && (
      <div className={`mb-4 md:mb-5 ${showMobileDetail ? 'hidden md:block' : ''}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1">Archive</p>
            <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">Chat History</h1>
            <p className="text-sm text-muted-foreground mt-1.5">
              {dateFilter === 'today' ? "Showing today's conversations" : 'Search transcripts, lead notes, and outcomes from past chats.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dateFilter && (
              <Button variant="outline" size="sm" onClick={() => { searchParams.delete('filter'); setSearchParams(searchParams) }}>
                <XCircle className="w-4 h-4 mr-2" />
                Clear Filter
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              disabled={conversations.length === 0}
              className="hidden md:flex"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 md:gap-6 flex-1 min-h-0">
        {/* Conversations Sidebar - hidden on mobile when viewing detail */}
        <div className={`w-full md:w-96 glass rounded-lg border flex flex-col overflow-hidden ${showMobileDetail ? 'hidden md:flex' : 'flex'}`}>
          {/* Sidebar Header */}
          <div className="p-4 border-b bg-muted/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-brand-primary" />
                <h2 className="font-semibold text-foreground">Conversations</h2>
                <Badge variant="secondary">{conversations.length}</Badge>
              </div>
            </div>

            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => {
                  const val = e.target.value
                  setSearchQuery(val)
                  if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
                  searchTimerRef.current = setTimeout(() => setDebouncedSearch(val), 300)
                }}
                className="pl-9"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Live</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conversations List */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="p-8 text-center">
                <p className="text-muted-foreground text-sm">Loading conversations...</p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-8 text-center">
                <Archive className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">
                  {searchQuery ? 'No conversations found' : 'No conversations yet'}
                </p>
              </div>
            ) : (
              <div className="p-2 space-y-2">
                {filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    onClick={() => handleConversationSelect(conversation)}
                    className={`group/row p-3 rounded-lg cursor-pointer transition-colors active:scale-[0.98] ${
                      selectedConversation?.id === conversation.id
                        ? 'bg-brand-primary text-white shadow-md'
                        : 'md:hover:bg-muted border border-transparent md:hover:border-border'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="w-10 h-10 flex-shrink-0">
                        <AvatarFallback className={selectedConversation?.id === conversation.id ? 'bg-background text-brand-primary' : 'bg-brand-primary-light text-brand-primary'}>
                          {getInitials(conversation)}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-medium text-sm truncate ${selectedConversation?.id === conversation.id ? 'text-white' : 'text-foreground'}`}>
                            {getParticipantName(conversation)}
                          </span>
                          {getStatusBadge(conversation.status)}
                        </div>

                        {(conversation.snippet || conversation.metadata?.lastMessage || conversation.metadata?.snippet) ? (
                          <div className={`text-xs truncate ${selectedConversation?.id === conversation.id ? 'text-white/80' : 'text-muted-foreground'}`}>
                            {conversation.snippet || conversation.metadata?.lastMessage || conversation.metadata?.snippet}
                          </div>
                        ) : (
                          <div className={`text-xs ${selectedConversation?.id === conversation.id ? 'text-white/80' : 'text-muted-foreground'}`}>
                            {conversation.messageCount} message{conversation.messageCount !== 1 ? 's' : ''}
                          </div>
                        )}

                        <div className={`flex items-center gap-2 mt-1 text-xs ${selectedConversation?.id === conversation.id ? 'text-white/60' : 'text-muted-foreground'}`}>
                          <Calendar className="w-3 h-3" />
                          {formatDateInUserTz(conversation.createdAt, userTz, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>

                      {/* Delete button on row */}
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(conversation.id) }}
                        className={`flex-shrink-0 p-1.5 rounded-md opacity-0 group-hover/row:opacity-100 transition-opacity ${
                          selectedConversation?.id === conversation.id
                            ? 'hover:bg-white/20 text-white/70 hover:text-white'
                            : 'hover:bg-destructive/10 text-muted-foreground hover:text-destructive'
                        }`}
                        title="Delete conversation"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Pagination */}
          <div className="p-3 border-t bg-muted/50 flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">Page {currentPage + 1}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={conversations.length < pageSize}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Conversation Detail Area - shown on mobile only when viewing detail */}
        <div className={`flex-1 glass rounded-lg border flex flex-col overflow-hidden ${showMobileDetail ? 'flex' : 'hidden md:flex'}`}>
          {selectedConversation ? (
            <>
              {/* Conversation Header */}
              <div className="p-3 md:p-4 border-b bg-muted/30">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-2">
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    {/* Back button for mobile */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleBackToList}
                      className="md:hidden h-8 w-8 flex-shrink-0"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-brand-primary text-white">
                        {getInitialsFromId(selectedConversation.visitorId)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-1.5">
                        {editingName ? (
                          <div className="flex items-center gap-1">
                            <Input
                              value={editNameValue}
                              onChange={(e) => setEditNameValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false) }}
                              className="h-7 text-sm font-semibold w-48"
                              autoFocus
                            />
                            <button onClick={handleSaveName} className="p-1 hover:text-primary">
                              <Check className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <h3 className="font-semibold text-foreground">
                              {(() => {
                                const m = selectedConversation.metadata
                                if (m?.contactInfo?.firstName) {
                                  const fullName = m.contactInfo.lastName ? `${m.contactInfo.firstName} ${m.contactInfo.lastName}` : m.contactInfo.firstName
                                  return fullName + (m.contactInfo.companyName ? ` · ${m.contactInfo.companyName}` : '')
                                }
                                if (m?.firstName) return m.firstName
                                if (m?.name) return m.name
                                return 'Website Visitor'
                              })()}
                            </h3>
                            <button
                              onClick={() => {
                                const m = selectedConversation.metadata
                                const currentName = m?.name || (m?.contactInfo?.firstName ? `${m.contactInfo.firstName}${m.contactInfo.lastName ? ` ${m.contactInfo.lastName}` : ''}` : '')
                                setEditNameValue(currentName)
                                setEditingName(true)
                              }}
                              className="p-1 hover:text-primary text-muted-foreground/50 hover:text-muted-foreground"
                              aria-label="Edit visitor name"
                              title="Edit visitor name"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          </>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-2">
                        <span className="text-muted-foreground/50 font-mono">ID: {selectedConversation.visitorId.slice(-8)}</span>
                        <Calendar className="w-3 h-3" />
                        {formatDateInUserTz(selectedConversation.createdAt, userTz, { month: 'short', day: 'numeric', year: 'numeric' })}
                        {' '}
                        {formatTimeInUserTz(selectedConversation.createdAt, userTz)}
                        {selectedConversation.endedAt && (
                          <>
                            <span>→</span>
                            {formatTimeInUserTz(selectedConversation.endedAt, userTz)}
                          </>
                        )}
                      </p>
                      {(selectedConversation.metadata?.url || selectedConversation.metadata?.pageUrl) && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                          <Globe className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">
                            {(() => {
                              try {
                                return new URL(selectedConversation.metadata.url || selectedConversation.metadata.pageUrl).pathname || '/'
                              } catch {
                                return selectedConversation.metadata.url || selectedConversation.metadata.pageUrl
                              }
                            })()}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-1 md:gap-2 flex-shrink-0">
                    {getStatusBadge(selectedConversation.status)}
                    <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                      {selectedConversation.messages.length} messages
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-destructive border-destructive/30 hover:bg-destructive hover:text-white"
                      onClick={() => setDeleteTarget(selectedConversation.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-3 md:p-6 bg-muted/20">
                <div className="max-w-3xl mx-auto">
                  {selectedConversation.messages.length === 0 && (
                    <div className="flex flex-col items-center justify-center text-center py-16">
                      <MessageSquare className="h-10 w-10 text-muted-foreground/30 mb-3" />
                      <p className="text-muted-foreground font-medium">No messages exchanged</p>
                      <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                        A session was started but no messages were sent. The visitor may have connected and left before typing.
                      </p>
                    </div>
                  )}
                  {selectedConversation.messages.map((message, index) => {
                    const isAgent = message.senderType === 'agent'
                    const isBot = message.senderType === 'bot'
                    const isVisitor = message.senderType === 'visitor'

                    // Group consecutive messages from the same sender
                    const prevMessage = selectedConversation.messages[index - 1]
                    const isFirstInGroup = !prevMessage || prevMessage.senderType !== message.senderType
                    const nextMessage = selectedConversation.messages[index + 1]
                    const isLastInGroup = !nextMessage || nextMessage.senderType !== message.senderType

                    return (
                      <div
                        key={message.id}
                        className={`flex gap-3 ${isAgent ? 'flex-row-reverse' : 'flex-row'} ${isFirstInGroup ? 'mt-4' : 'mt-1'} animate-slide-in-left`}
                      >
                        {/* Avatar — only show on first message in group */}
                        {isFirstInGroup ? (
                          <Avatar className="w-8 h-8 flex-shrink-0">
                            {isVisitor ? (
                              <AvatarFallback className="bg-muted text-muted-foreground">
                                {getInitialsFromId(selectedConversation.visitorId)}
                              </AvatarFallback>
                            ) : isBot ? (
                              <AvatarFallback className="bg-brand-secondary text-white">
                                <Bot className="w-4 h-4" />
                              </AvatarFallback>
                            ) : (
                              <AvatarImage src={agent?.avatarUrl} alt={agent?.name} />
                            )}
                            {isAgent && !agent?.avatarUrl && (
                              <AvatarFallback className="bg-brand-primary text-white">
                                {agent?.name?.charAt(0) || 'A'}
                              </AvatarFallback>
                            )}
                          </Avatar>
                        ) : (
                          <div className="w-8 flex-shrink-0" />
                        )}

                        <div className={`flex flex-col ${isAgent ? 'items-end' : 'items-start'} max-w-[85%] sm:max-w-[70%]`}>
                          {/* Sender name — only show on first message in group */}
                          {isFirstInGroup && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-muted-foreground">
                                {isVisitor ? (selectedConversation.metadata?.contactInfo?.firstName || 'Website Visitor') : isBot ? 'AI Assistant' : 'Agent'}
                              </span>
                            </div>
                          )}

                          <div className="flex items-center gap-1 group/msg">
                            <div
                              className={`px-4 py-2.5 rounded-2xl shadow-sm ${
                                isAgent
                                  ? `bg-brand-primary text-white ${isFirstInGroup ? 'rounded-tr-sm' : ''} ${isLastInGroup ? '' : 'rounded-br-sm'}`
                                  : isBot
                                  ? `bg-brand-secondary text-white ${isFirstInGroup ? 'rounded-tl-sm' : ''} ${isLastInGroup ? '' : 'rounded-bl-sm'}`
                                  : `bg-background text-foreground border ${isFirstInGroup ? 'rounded-tl-sm' : ''} ${isLastInGroup ? '' : 'rounded-bl-sm'}`
                              }`}
                            >
                              <p className="text-sm leading-relaxed">{message.content}</p>
                            </div>
                            {isVisitor && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="opacity-0 group-hover/msg:opacity-100 transition-opacity p-1 hover:text-destructive" title="Flag message">
                                    <Flag className="h-3 w-3" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-40">
                                  <DropdownMenuItem onClick={() => handleFlagMessage(selectedConversation.id, message.id, 'spam', selectedConversation.visitorId)}>
                                    Spam
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleFlagMessage(selectedConversation.id, message.id, 'off_topic', selectedConversation.visitorId)}>
                                    Off-Topic
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleFlagMessage(selectedConversation.id, message.id, 'suspicious', selectedConversation.visitorId)}>
                                    Suspicious
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>

                          {/* Timestamp — only show on last message in group */}
                          {isLastInGroup && (
                            <span className="text-xs text-muted-foreground/60 mt-1 px-1">
                              {formatTimeInUserTz(message.timestamp, userTz)}
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}

                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Footer Info */}
              <div className="p-3 border-t bg-muted/50 space-y-2">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-4">
                    <span>Conversation ID: {selectedConversation.id.slice(0, 8)}...</span>
                    {selectedConversation.agentId && (
                      <span className="flex items-center gap-1">
                        <UserCircle className="w-4 h-4" />
                        Agent handled
                      </span>
                    )}
                  </div>
                  {selectedConversation.endedAt && selectedConversation.createdAt && (
                    <span>
                      Duration: {formatDuration(
                        new Date(selectedConversation.endedAt).getTime() - new Date(selectedConversation.createdAt).getTime()
                      )}
                    </span>
                  )}
                </div>

                {/* UTM Parameters */}
                {selectedConversation.metadata?.utm && (
                  <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                    <span className="font-medium text-foreground">UTM Tracking:</span>
                    {selectedConversation.metadata.utm.utmSource && (
                      <span className="px-2 py-1 bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded">
                        Source: {selectedConversation.metadata.utm.utmSource}
                      </span>
                    )}
                    {selectedConversation.metadata.utm.utmMedium && (
                      <span className="px-2 py-1 bg-green-500/10 dark:bg-green-500/20 text-green-600 dark:text-green-400 rounded">
                        Medium: {selectedConversation.metadata.utm.utmMedium}
                      </span>
                    )}
                    {selectedConversation.metadata.utm.utmCampaign && (
                      <span className="px-2 py-1 bg-purple-500/10 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 rounded">
                        Campaign: {selectedConversation.metadata.utm.utmCampaign}
                      </span>
                    )}
                    {selectedConversation.metadata.utm.utmTerm && (
                      <span className="px-2 py-1 bg-yellow-500/10 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 rounded">
                        Term: {selectedConversation.metadata.utm.utmTerm}
                      </span>
                    )}
                    {selectedConversation.metadata.utm.utmContent && (
                      <span className="px-2 py-1 bg-pink-500/10 dark:bg-pink-500/20 text-pink-600 dark:text-pink-400 rounded">
                        Content: {selectedConversation.metadata.utm.utmContent}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <Archive className="w-16 h-16 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No conversation selected</h3>
              <p className="text-sm text-muted-foreground">Choose a conversation from the sidebar to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Conversation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this conversation? All messages will be permanently removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirmed} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Deleting...
                </>
              ) : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
